import { useState, useMemo } from "react";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { post } from "@/lib/api";
import { formatRelativeTime } from "@/lib/time";
import { useToast } from "@/components/ToastProvider";

export type CommentResponderProps = {
  platform: string;
  postId: string;
  commentId: string;
  commentText: string;
  commenter: string;
  createdAt?: string;
  hitlEnabled?: boolean;
  previewUrl?: string;
  onSuccess?: () => void;
};

const TONE_OPTIONS = ["friendly", "professional", "playful", "mystical", "hype", "calm"];

export function CommentResponder({
  platform,
  postId,
  commentId,
  commentText,
  commenter,
  createdAt,
  hitlEnabled = true,
  previewUrl,
  onSuccess,
}: CommentResponderProps) {
  const { push } = useToast();
  const [tone, setTone] = useState<string>("friendly");
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const friendlyPlatform = useMemo(() => platform.replace(/_/g, " ").toUpperCase(), [platform]);

  const generateDraft = async () => {
    setLoading(true);
    setError(null);
    setStatusMessage(null);
    try {
      const response = await post("/marketing/comments/respond", {
        platform,
        post_id: postId,
        comment_id: commentId,
        tone,
        hitl: true,
        comment_preview: commentText,
      });
      setDraft(response.draft ?? "");
      setStatusMessage(response.message ?? "Draft ready for approval.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to draft reply.");
    } finally {
      setLoading(false);
    }
  };

  const sendResponse = async (hitl: boolean) => {
    if (!draft.trim()) {
      setError("Generate or type a reply first.");
      return;
    }
    setSending(true);
    setError(null);
    try {
      const response = await post("/marketing/comments/respond", {
        platform,
        post_id: postId,
        comment_id: commentId,
        tone,
        hitl,
        comment_preview: commentText,
        response_text: draft,
      });
      setStatusMessage(response.message ?? (hitl ? "Sent for approval." : "Response posted."));
      if (!hitl && response.status === "sent") {
        push({ title: "Response queued", message: `Auto-replied on ${friendlyPlatform}.` });
        onSuccess?.();
      }
      if (hitl && response.status === "hitl_pending") {
        push({ title: "Approval required", message: "Queued for HITL review." });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to send response.");
    } finally {
      setSending(false);
    }
  };

  const clearDraft = () => {
    setDraft("");
    setStatusMessage(null);
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-border/80 bg-background/40 p-4 text-sm text-foreground/70">
        <div className="flex items-center justify-between gap-2">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Incoming comment</p>
            <p className="text-foreground">{commentText}</p>
          </div>
          <Badge variant="outline" className="border-border text-foreground/80">
            @{commenter}
          </Badge>
        </div>
        {createdAt ? <p className="mt-2 text-xs text-muted-foreground">{formatRelativeTime(createdAt)}</p> : null}
        {previewUrl ? (
          <div className="mt-3 rounded-xl border border-border/70 bg-background/40 p-2">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Post preview</p>
            <img src={previewUrl} alt="Post preview" className="mt-2 w-full rounded-lg object-cover" />
          </div>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="space-y-1">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Tone</p>
          <Select value={tone} onValueChange={setTone}>
            <SelectTrigger className="w-40 bg-background/40 text-foreground">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-background/40 text-foreground">
              {TONE_OPTIONS.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1" />
        <Button onClick={generateDraft} disabled={loading} variant="secondary">
          {loading ? "Drafting…" : "Generate Draft"}
        </Button>
      </div>

      <div className="space-y-2">
        <label className="text-xs uppercase tracking-wide text-muted-foreground">AI Draft</label>
        <Textarea
          rows={4}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          className="bg-background/40 text-foreground"
          placeholder="Generate or type your response"
        />
      </div>

      {statusMessage ? <p className="text-xs text-muted-foreground">{statusMessage}</p> : null}
      {error ? <p className="text-xs text-destructive">{error}</p> : null}

      <div className="flex flex-wrap gap-2">
        <Button onClick={() => sendResponse(false)} disabled={sending || !draft.trim()}>
          {sending ? "Sending…" : "Send now"}
        </Button>
        {hitlEnabled ? (
          <Button
            variant="secondary"
            onClick={() => sendResponse(true)}
            disabled={sending || !draft.trim()}
          >
            Queue for approval
          </Button>
        ) : null}
        <Button variant="outline" onClick={clearDraft}>
          Reject draft
        </Button>
      </div>
    </div>
  );
}
